import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import type { Post } from '@/shared/types/api';
import { readingTime } from '@/shared/utils/readingTime';

interface PostCardProps {
  post: Post;
}

export default function PostCard({ post }: PostCardProps) {
  const date = post.published_at ?? post.created_at;
  const minutes = post.content_markdown
    ? readingTime(post.content_markdown)
    : post.excerpt
      ? Math.ceil(post.excerpt.split(/\s+/).length / 200) + 1
      : null;

  return (
    <article className="py-6" style={{ borderBottom: '1px solid var(--color-border)' }}>
      <h3 className="text-xl font-bold m-0 leading-snug post-title-decoration">
        <Link to={`/blog/${post.id}/${post.slug}`} className="text-accent no-underline hover:underline">
          {post.title}
        </Link>
      </h3>

      <div className="text-sm text-muted mt-3">
        {format(new Date(date), 'yyyy.MM.dd')} :: Vladyslav Chebotarov
        {minutes && (
          <span> :: {minutes} min read</span>
        )}
      </div>

      {(post.categories?.length > 0 || post.tags.length > 0) && (
        <div className="flex flex-wrap gap-2 mt-2">
          {post.categories?.map((cat) => (
            <Link key={`cat-${cat.id}`} to={`/category/${cat.id}/${cat.slug}`} className="text-muted text-sm no-underline hover:underline">
              [{cat.name}]
            </Link>
          ))}
          {post.tags.map((tag) => (
            <Link key={tag.id} to={`/tag/${tag.id}/${tag.slug}`} className="text-accent text-sm no-underline hover:underline">
              #{tag.name}
            </Link>
          ))}
        </div>
      )}

      {post.excerpt && (
        <p className="mt-4 leading-relaxed m-0 text-foreground">{post.excerpt}</p>
      )}

      <div className="mt-3">
        <Link to={`/blog/${post.id}/${post.slug}`} className="text-accent no-underline hover:underline">
          read more &rarr;
        </Link>
        {post.comment_count > 0 && (
          <Link
            to={`/blog/${post.id}/${post.slug}#comments`}
            className="ml-3 text-accent no-underline hover:underline"
          >
            {post.comment_count} comment{post.comment_count !== 1 ? 's' : ''}
          </Link>
        )}
      </div>
    </article>
  );
}
